import { inferProcedureInput } from "@trpc/server";
import classNames from "classnames";
import { string } from "prop-types";
import { useContext, useState } from "react";
import { Link } from "react-router-dom";
import { common_utils } from "../../../../../../common/common_utils";
import { Constants } from "../../../../../../common/constants";
import { GuildData } from "../../../../../../common/sesh_types";
import { RouterInferenceHelpers } from "../../../../../../src/api/trpc/rootRouter";
import { DashboardUtils } from "../../../dashboard_utils";
import { GuildListContext } from "../../../sesh_router";
import { trpc } from "../../../utils/trpc";
import { ModifySettingsButton } from "../base_components/modify_settings_button";
import { InfoTooltip } from "../tooltips/info_tooltip";

type ActivatedGuilds =
	RouterInferenceHelpers["premium"]["getUserPremiumDetails"]["output"]["activated_guilds"];

export function PremiumServerListingsModal(props: {
	premium_details: RouterInferenceHelpers["premium"]["getUserPremiumDetails"]["output"];
	onClose: () => void;
}) {
	const subtitle = (
		<div className="flex items-center justify-center justify-items-center gap-2">
			<div className="flex items-center gap-2">
				{props.premium_details.transferrable_activated_servers_count}/
				{props.premium_details.max_activated_servers} transferrable
				activations
				<InfoTooltip
					info={
						<>
							You can activate servers here or by running the{" "}
							<span className="command-example">/premium</span>{" "}
							command in a server
						</>
					}
				/>
			</div>
		</div>
	);

	return (
		<DashboardUtils.SeshModal
			is_active={true}
			onClose={props.onClose}
			title="Manage Premium Servers"
			subtitle={subtitle}
			content={
				<PremiumServersListingContent
					activated_guilds={props.premium_details.activated_guilds}
				/>
			}
		/>
	);
}

function PremiumServersListingContent(props: {
	activated_guilds: ActivatedGuilds;
}) {
	const guild_list = useContext(GuildListContext);

	const modifiable_server_ids = props.activated_guilds.map((val) => val.id);
	const filtered_guild_list = guild_list.filter(
		(val) => !modifiable_server_ids.includes(val.id) && val.has_sesh
	);

	// TODO: get the icon URL so we can display proper image
	return (
		<div className="mt-4 flex h-fit max-h-[90%] min-h-0 overflow-auto">
			<ul className="flex min-h-0 w-full flex-col gap-2 overflow-auto">
				{props.activated_guilds.map((val) => (
					<ServerListing
						key={val.id}
						{...val}
						icon={
							guild_list.find((data) => data.id === val.id)?.icon
						}
						is_deactivatable
					/>
				))}
				{filtered_guild_list.map((val) => (
					<ServerListing
						key={val.id}
						{...val}
						is_deactivatable={false}
						is_discord_entitlement={false}
					/>
				))}
			</ul>
		</div>
	);
}

function ServerListing(props: {
	name: string;
	id: string;
	icon?: string;
	is_deactivatable: boolean;
	is_discord_entitlement: boolean;
}) {
	const icon = DashboardUtils.GetGuildIconURI(
		{ id: props.id, icon: props.icon },
		64
	);

	const cta = props.is_deactivatable ? (
		<SeverListingPremiumButton {...props} />
	) : (
		<Link to={common_utils.getGuildSettingsURL(props.id, "premium")}>
			<ModifySettingsButton>Activate</ModifySettingsButton>
		</Link>
	);

	return (
		<li className="flex items-center gap-2 rounded-sm bg-base-light shadow-sm">
			<div className="h-full w-[5rem] rounded-l-sm bg-base-dark p-2">
				<figure className="image flex h-full w-full items-center">
					<img className="is-rounded h-full w-full" src={icon}></img>
				</figure>
			</div>
			<div className="flex w-full flex-col items-center gap-2 p-2 sm:flex-row">
				<div className="flex flex-col">
					<div className="text-center text-base sm:text-left sm:text-lg">
						<span className="font-bold">{props.name}</span>
					</div>
					<div className="text-xs">
						<span className="font-mono">{props.id}</span>
					</div>
				</div>
				<div className="sm:ml-auto">{cta}</div>
			</div>
		</li>
	);
}

function SeverListingPremiumButton(props: ActivatedGuilds[number]) {
	const [is_loading, setIsLoading] = useState(false);

	const mutation = trpc.premium.modifyPremiumStatus.useMutation();

	const context = trpc.useContext();

	if (props.is_discord_entitlement) {
		return (
			<div className="flex flex-col">
				<div>Discord Subscription</div>
				<div className="ml-auto">
					<a
						className="manual-link"
						target="_blank"
						href={Constants.DISCORD_PREMIUM_MANAGEMENT_FAQ_LINK}
					>
						Manage <i className="fas fa-external-link-alt" />
					</a>
				</div>
			</div>
		);
	}

	return (
		<button
			className={classNames(
				"button dashboard-warning-button ml-auto w-fit",
				{ "is-loading": is_loading }
			)}
			onClick={() => {
				setIsLoading(true);
				mutation.mutate(
					{
						guild_id: props.id,
						modification: "Deactivate",
					},
					{
						onSuccess: () => {
							setIsLoading(false);
							context.premium.getUserPremiumDetails.invalidate();
						},
					}
				);
			}}
		>
			Deactivate
		</button>
	);
}
